import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import DogCard from "../components/DogCard";
import { HandHeart, Heart, ArrowRight } from "lucide-react";

export default function FosterPortal() {
    const { user } = useAuth();
    const [dogs, setDogs] = useState([]);
    const [pledges, setPledges] = useState([]);

    useEffect(() => {
        api.get("/dogs", { params: { at_risk: true } }).then((r) => setDogs(r.data));
        if (user) api.get("/pledges/mine").then((r) => setPledges(r.data.filter((p) => p.type === "foster")));
    }, [user]);

    return (
        <div data-testid="foster-portal">
            <div className="bg-[var(--rl-cream)] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="eyebrow mb-3">Foster Portal</div>
                        <h1 className="font-serif text-4xl lg:text-6xl tracking-tight">Open your home,<br />save a life.</h1>
                        <p className="mt-4 text-[var(--rl-text-2)] max-w-md">Fostering is the single biggest lever in rescue. Give a dog a soft place to land while they wait for their forever home.</p>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-[var(--rl-border)]">
                        <img src="https://images.unsplash.com/photo-1579119134757-5c38803f34fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHx2b2x1bnRlZXIlMjBodWdnaW5nJTIwZG9nfGVufDB8fHx8MTc4NTQ2MjkxNXww&ixlib=rb-4.1.0&q=85"
                             alt="Foster hugs dog" className="w-full aspect-[4/3] object-cover" />
                    </div>
                </div>
            </div>

            {user && (
                <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                    <div className="grid grid-cols-3 gap-4 max-w-2xl">
                        <div className="card-warm rounded-lg p-5"><div className="eyebrow">Active fosters</div><div className="font-serif text-3xl mt-1">{pledges.length}</div></div>
                        <div className="card-warm rounded-lg p-5"><div className="eyebrow">Lives touched</div><div className="font-serif text-3xl mt-1">{pledges.length}</div></div>
                        <div className="card-warm rounded-lg p-5 bg-[var(--rl-cream)]"><div className="eyebrow">Foster since</div><div className="font-serif text-xl mt-1">{new Date(user.created_at).getFullYear()}</div></div>
                    </div>
                </section>
            )}

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="eyebrow mb-2">Dogs needing foster</div>
                        <h2 className="font-serif text-3xl">Can you take one home tonight?</h2>
                    </div>
                    <Link to="/at-risk" className="text-sm text-[var(--rl-primary)] font-semibold hover:underline inline-flex items-center gap-1">
                        See all at-risk <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {dogs.slice(0, 8).map((d) => <DogCard key={d.id} dog={d} urgent />)}
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                <div className="eyebrow mb-3">How fostering works</div>
                <h2 className="font-serif text-3xl">Three simple steps.</h2>
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                    <Step n="01" title="Signal your capacity" body="Tell us your size, energy, and household preferences. We match you when a dog fits." />
                    <Step n="02" title="Bring them home" body="Rescue provides food, crate, and vet care. You provide the couch." />
                    <Step n="03" title="Say goodbye… and repeat" body="Foster until they're adopted. Then help the next one." />
                </div>
            </section>
        </div>
    );
}

function Step({ n, title, body }) {
    return (
        <div className="card-warm rounded-xl p-6">
            <div className="font-serif text-4xl text-[var(--rl-primary)]">{n}</div>
            <h3 className="font-serif text-2xl mt-3">{title}</h3>
            <p className="text-sm text-[var(--rl-text-2)] mt-2">{body}</p>
        </div>
    );
}
